import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { ButtonComponent, ModalFooterDirective, TableCellDirective, TableGridComponent, TableHeader } from 'mbs-ui-kit';
import { ModalComponent } from 'mbs-ui-kit/modal/modal.component';
import { MbsPopupType } from 'mbs-ui-kit/utils/enums/mbs-popup-enum';
import { MbsSize } from 'mbs-ui-kit/utils/enums/mbs-size-enum';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { catchError, filter, of, take } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-grant-license-for-computer-modal',
  templateUrl: './grant-license-for-computer-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    NgbTooltipModule,
    ModalComponent,
    TableCellDirective,
    TableGridComponent,
    ModalFooterDirective
  ]
})
export class GrantLicenseForComputerModalComponent implements OnInit {
  i18nextService = inject(I18NextService);
  i18nPipe = inject(I18NextPipe);
  private licensesFacade = inject(LicensesFacade);
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private computersFacade = inject(ComputersFacade);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  readonly MbsSize = MbsSize;
  readonly AlertType = MbsPopupType;

  public loading = false;
  public licenseTypesLoading = true;

  public headers: TableHeader[] = [
    { name: 'License type', gridColSize: '5fr' },
    { name: 'Count', class: '-end', gridColSize: '3fr' },
    { name: '', class: '-end', gridColSize: '2fr' }
  ];

  licensesTypes: Array<{
    id: string;
    name: string;
  }>;

  ngOnInit(): void {
    toObservable(this.licensesSignalFacade.allLicenseTypes)
      .pipe(filter(Boolean), take(1))
      .subscribe((types) => {
        this.licensesTypes = this.baseModal.data.availableLicenses.map((license) => {
          const type = types.find((l) => l.type === license.licenseType);

          return {
            count: license.count,
            id: type.type,
            name: type.displayName
          };
        });
        this.licenseTypesLoading = false;
        this.cdr.detectChanges();
      });
  }

  grantLicense(id: string): void {
    this.loading = true;
    this.licensesFacade
      .grantLicenseByType(this.baseModal?.data.hid, id)
      .pipe(catchError(() => of(false)))
      .subscribe((result) => {
        this.loading = false;
        this.computersFacade.loadComputerByHid({ hid: this.baseModal?.data.hid, force: true });
        result && this.baseModal.close();
      });
  }

  buyLicense(): void {
    void this.router.navigate([RoutingPath.ApLicenses], { queryParams: { buy: 'true' } });
  }
}
